import React, { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Text, Icon, useColorModeValue } from '@chakra-ui/react';
import '../../styles/reskinned-sidebar.css';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {
  SalesIcon,
  SalesSolidIcon,
  HomeIcon,
  HomeSolidIcon,
  ListViewIcon,
  MoneyIcon,
  SettingsIcon,
  SettingsSolidIcon,
  ReportsIcon,
  ReportsSolidIcon,
  TargetAudienceIcon,
  TargetAudienceSolidIcon,
  AtomIcon,
  SummaryIcon,
  SummarySolidIcon,
  GridViewIcon,
  ExternalLinkIcon,
} from '@spotoninc/nexus-web-assets';
import { useNexus } from '@spotoninc/nexus-react';
import { useFeatureFlagsFromContext } from '../../launchdarkly';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { createViewToUrlMap } from '../../config/routes';
import { getBlid } from '../../utils/cookieHelpers';
import {
  getSpotOnDashboardUrl,
  isDevOrQaEnvironment,
} from '../../utils/spotOnUrls';
import { trackEvent, AnalyticsEventNames } from '../../utils/analytics';

const ReskinnedSidebar = ({ onClick, view }) => {
  const { theme } = useNexus();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [financialInsightsOpen, setFinancialInsightsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [permittedScreens, setPermittedScreens] = useState([]);

  // Get the feature flags using the optimized context approach
  const flags = useFeatureFlagsFromContext();
  const showReskin = flags?.showReskin || false;
  const slimFeaturesOnly = flags?.slimFeaturesOnly || false;

  // Only apply slimFeaturesOnly if showReskin is true
  const shouldApplySlimFeatures = showReskin && slimFeaturesOnly;

  // Check if user is a SpotOn customer (has PA_BLID cookie)
  const isSpotOnCustomer = !!getBlid();

  const isDevOrQA = isDevOrQaEnvironment();

  const spotOnDashboardUrl = getSpotOnDashboardUrl();

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const activeBgColor = useColorModeValue('blue.50', 'blue.900');
  const activeTextColor = useColorModeValue('blue.600', 'blue.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Helper function to check if a view is part of Financial Insights
  const isFinancialInsightsView = currentView => {
    return (
      currentView === 'reskinnedLocationAnalysis' ||
      currentView === 'reskinnedComparisonOvertime' ||
      currentView === 'financialAnalysis'
    ); // Include the parent key
  };

  // Helper function to check if a view is part of Settings
  const isSettingsView = currentView => {
    return [
      'reskinnedManageUsers',
      'integrationSettings',
      'reskinnedAccountAlias',
      'reskinnedRoleSetting',
      'reskinnedPrivilegeSettings',
      'exclusionSettings',
      'reskinnedExclusionSettings',
      'locationAiMapping', // Add the location AI mapping key
      'budgetUpdate',
      'reskinnedBudgetUpdateDD',
      'setting', // Include the parent setting key
    ].includes(currentView);
  };

  // Helper function to check if user has permission for a screen
  const hasPermission = screenKey => {
    // Special case for 'tasks' - check for both 'task' and 'tasks'
    const keysToCheck =
      screenKey === 'tasks'
        ? ['task', 'tasks']
        : screenKey === 'task'
          ? ['task', 'tasks']
          : [screenKey];

    // Special case for budget screens - check for both the item key and 'budgetDashboard'
    // since the budget screen is named differently in production than other environments
    const budgetKeys = ['budget', 'budgetUpdate', 'budgetUpdateDD'];
    if (budgetKeys.includes(screenKey)) {
      keysToCheck.push('budgetDashboard');
    }

    const hasPermission = permittedScreens.some(screen => {
      // Check for exact match with any of the keys to check
      if (keysToCheck.some(key => screen.key === key)) {
        return true;
      }

      // Check if this is a parent screen with children
      if (screen.child && screen.child.length > 0) {
        // Check if any child matches any of the keys to check
        return screen.child.some(childScreen => {
          return keysToCheck.some(key => childScreen.key === key);
        });
      }

      return false;
    });

    return hasPermission;
  };

  // Map view names to URL routes (memoized for performance)
  const viewToUrlMap = useMemo(() => createViewToUrlMap(), []);

  // Helper function to navigate to the appropriate route
  const handleNavigation = viewName => {
    const targetUrl = viewToUrlMap[viewName];
    if (targetUrl) {
      navigate(targetUrl);
    } else {
      // Fallback to onClick if URL mapping not found
      onClick(viewName);
    }
  };

  // Helper function to render the appropriate icon based on the current view
  const renderIcon = (viewName, RegularIcon, SolidIcon = null) => {
    // Check if the current view matches the viewName or is a child view
    let isActive = false;
    // Handle special cases for parent menus
    if (viewName.toLowerCase() === 'financialinsights') {
      isActive = isFinancialInsightsView(view);
    } else if (viewName.toLowerCase() === 'settings') {
      isActive = isSettingsView(view);
    } else {
      // For regular menu items, check if view matches the lowercase version of viewName
      isActive = view.toLowerCase() === viewName.toLowerCase();
    }

    // Special cases for AI Summary items
    if (viewName === 'aiSummary' && view === 'aisummary') {
      isActive = true;
    } else if (viewName === 'aiSummaryOD' && view === 'aisummaryod') {
      isActive = true;
    }

    const iconColor = isActive
      ? theme.tokens.semanticColorTextBrandStrongNone
      : theme.tokens.semanticColorTextNeutralStaticNone;

    // If SolidIcon is provided and view is active, use SolidIcon
    // Otherwise use RegularIcon
    if (SolidIcon && isActive) {
      return <SolidIcon size="1.5rem" color={iconColor} />;
    }

    return <RegularIcon size="1.5rem" color={iconColor} />;
  };

  // Create base menu items with explicit keys
  const baseMenuItems = [
    {
      customIcon: renderIcon('dashboard', HomeIcon, HomeSolidIcon),
      label: 'Dashboard',
      onClick: () => handleNavigation('dashboard'),
      key: 'dashboard',
    },
    {
      customIcon: renderIcon('task', ListViewIcon),
      label: 'Tasks',
      onClick: () => handleNavigation('task'),
      key: 'tasks',
    },
    {
      customIcon: renderIcon('reskinnedCost', MoneyIcon),
      label: 'Cost',
      onClick: () => handleNavigation('reskinnedCost'),
      key: 'cost',
    },
  ];

  // Budget menu item - only included if slimFeaturesOnly is false
  const budgetMenuItem = {
    customIcon: renderIcon('budget', SalesIcon, SalesSolidIcon),
    label: 'Budget',
    onClick: () => handleNavigation('budget'),
    key: 'budget',
  };

  // Benchmark menu item - only included if slimFeaturesOnly is false
  const benchmarkMenuItem = {
    customIcon: renderIcon(
      'benchmark',
      TargetAudienceIcon,
      TargetAudienceSolidIcon
    ),
    label: 'Benchmark',
    onClick: () => handleNavigation('benchmark'),
    key: 'benchmark',
  };

  // Final menu items array - conditionally include Budget and Benchmark based on feature flag
  // and filter based on user permissions
  const menuItems = shouldApplySlimFeatures
    ? baseMenuItems
    : [
        baseMenuItems[0],
        budgetMenuItem,
        ...baseMenuItems.slice(1),
        ...(hasPermission('benchmark') ? [benchmarkMenuItem] : []),
      ];

  // Add AI Summary and AI Summary On-Demand to the menu items
  const aiSummaryMenuItem = {
    icon: SummaryIcon,
    solidIcon: SummarySolidIcon,
    label: 'AI Summary',
    onClick: () => handleNavigation('aiSummary'),
    key: 'aiSummary',
  };

  const aiSummaryODMenuItem = {
    icon: AtomIcon,
    solidIcon: AtomIcon, // Use the same icon since there's no solid variant
    label: 'AI On-Demand',
    onClick: () => handleNavigation('aiSummaryOD'),
    key: 'aiSummaryOD',
  };

  // Menu items already have explicit keys, no need for extraction
  const menuItemsWithKeys = menuItems;

  // Filter menu items based on permissions
  // If no permissions are loaded yet, show all items
  const filteredMenuItems =
    permittedScreens.length === 0
      ? menuItemsWithKeys
      : menuItemsWithKeys.filter(item => hasPermission(item.key));

  // Special handling for Task menu item - it might have a different key in the API
  // Make sure Tasks is always included if the user has permission to 'task'
  const taskMenuItem = menuItemsWithKeys.find(item => item.key === 'tasks');
  const hasTaskPermission = hasPermission('task');

  // Add AI items if user has permission
  const menuItemsWithAI = [
    ...filteredMenuItems,
    // Add task item if it's not already included and user has permission
    ...(hasTaskPermission &&
    !filteredMenuItems.some(item => item.key === 'tasks')
      ? [taskMenuItem]
      : []),
    ...(hasPermission('aiSummary') ? [aiSummaryMenuItem] : []),
    ...(hasPermission('aiSummaryOD') ? [aiSummaryODMenuItem] : []),
  ].filter(Boolean); // Filter out any undefined items

  // Create base settings items with explicit keys
  const baseSettingsItems = [
    {
      label: 'Manage Users',
      onClick: () => handleNavigation('reskinnedManageUsers'),
      key: 'manageUsers',
    },
    {
      label: 'Change Account Alias',
      onClick: () => handleNavigation('reskinnedAccountAlias'),
      key: 'accountAlias',
    },
    {
      label: 'Manage Integrations',
      onClick: () => handleNavigation('integrationSettings'),
      key: 'integrationSettings',
    },
    {
      label: 'Define Role',
      onClick: () => handleNavigation('reskinnedRoleSetting'),
      key: 'roleSetting',
    },
    {
      label: 'Assign Privilege Settings',
      onClick: () => handleNavigation('reskinnedPrivilegeSettings'),
      key: 'privilegeSettings',
    },
    {
      label: 'Exclusions',
      onClick: () => handleNavigation('reskinnedExclusionSettings'),
      key: 'exclusionSettings',
    },
    {
      label: 'Exclusion Settings',
      onClick: () => handleNavigation('reskinnedExclusionSettings'),
      key: 'exclusionSettingsNew',
    },
    // {
    //   label: 'Location AI Mapping',
    //   onClick: () => handleNavigation('locationAiMapping'),
    //   key: 'locationAiMapping',
    // },
  ];

  // Budget settings item - only included if slimFeaturesOnly is false
  const budgetSettingsItem = {
    label: 'Budget',
    onClick: () => handleNavigation('budgetUpdate'),
    key: 'budgetUpdate',
  };

  // Settings items already have explicit keys, no need for extraction
  const baseSettingsItemsWithKeys = baseSettingsItems;

  // Filter settings items based on permissions
  // If no permissions are loaded yet, show all items
  const filteredBaseSettingsItems =
    permittedScreens.length === 0
      ? baseSettingsItemsWithKeys
      : baseSettingsItemsWithKeys.filter(item => hasPermission(item.key));

  // Budget settings item already has a key, just use it directly
  const budgetSettingsItemWithKey = budgetSettingsItem;

  // Add budget drill down settings item
  const budgetDDSettingsItem = {
    label: 'Budget Drill Down',
    onClick: () => handleNavigation('reskinnedBudgetUpdateDD'),
    key: 'budgetUpdateDD',
  };

  // Final settings items array - conditionally include Budget based on feature flag and permissions
  const settingsItems = shouldApplySlimFeatures
    ? filteredBaseSettingsItems
    : [
        ...filteredBaseSettingsItems,
        ...(hasPermission('budgetUpdate') ? [budgetSettingsItemWithKey] : []),
        ...(hasPermission('budgetUpdateDD') ? [budgetDDSettingsItem] : []),
      ];

  // Add keys to financial insights items for permission checking
  const financialInsightsItemsWithKeys = [
    {
      label: 'Location Analysis',
      onClick: () => handleNavigation('reskinnedLocationAnalysis'),
      key: 'locationAnalysis',
    },
    {
      label: 'Comparison Overtime',
      onClick: () => handleNavigation('reskinnedComparisonOvertime'),
      key: 'dateAnalysis',
    },
  ];

  // Filter financial insights items based on permissions
  // If no permissions are loaded yet, show all items
  const financialInsightsItems =
    permittedScreens.length === 0
      ? financialInsightsItemsWithKeys
      : financialInsightsItemsWithKeys.filter(item => hasPermission(item.key));

  // Check if we're in mobile view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  // In mobile view, always expand the sidebar
  useEffect(() => {
    if (isMobile) {
      setIsExpanded(true);
    }
  }, [isMobile]);

  // Fetch screens the user has permission to access
  useEffect(() => {
    fetchWithAuth('/api/screens/', {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        if (data.code === undefined) {
          setPermittedScreens(data['screen_list']);
        } else {
          window.open('/login', '_self');
          alert('Session Expired!.');
        }
      })
      .catch(() => {});
  }, []);

  // Auto-expand Financial Insights dropdown when a Financial Insights view is active
  useEffect(() => {
    if (isFinancialInsightsView(view)) {
      setFinancialInsightsOpen(true);
    }
  }, [view]);

  // Auto-expand Settings dropdown when a Settings view is active
  useEffect(() => {
    if (isSettingsView(view)) {
      setSettingsOpen(true);
    }
  }, [view]);

  return (
    <Box
      as="nav"
      role="navigation"
      aria-label="Main navigation"
      w={isExpanded ? '280px' : '80px'}
      h="calc(100vh - 70px)" /* Adjust height to account for top navbar */
      bg={bgColor}
      borderRight="1px"
      borderColor={borderColor}
      py={5}
      px={4}
      transition="all 0.3s ease"
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
      position="fixed"
      left={0}
      top="70px" /* Position below the top navbar */
      zIndex={1000}
      className="reskinned-sidebar"
    >
      <Flex direction="column" h="100%" overflow="hidden">
        {/* Main Menu Items */}
        <Flex
          direction="column"
          gap={1}
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: borderColor,
              borderRadius: '24px',
            },
          }}
        >
          {menuItemsWithAI.map((item, index) => (
            <Flex
              key={index}
              align="center"
              p={3}
              borderRadius="lg"
              cursor="pointer"
              _hover={{ bg: activeBgColor }}
              onClick={item.onClick}
              bg={view === item.key ? activeBgColor : 'transparent'}
              color={view === item.key ? activeTextColor : textColor}
              transition="all 0.2s ease"
            >
              {item.icon ? (
                <Box mr={3}>
                  {view === item.key && item.solidIcon ? (
                    <item.solidIcon
                      size="1.5rem"
                      color={theme.tokens.semanticColorTextBrandStrongNone}
                    />
                  ) : (
                    <item.icon
                      size="1.5rem"
                      color={
                        view === item.key
                          ? theme.tokens.semanticColorTextBrandStrongNone
                          : theme.tokens.semanticColorTextNeutralStaticNone
                      }
                    />
                  )}
                </Box>
              ) : item.customIcon ? (
                <Box mr={3}>{item.customIcon}</Box>
              ) : (
                <Icon as={item.icon} mr={3} />
              )}
              <Text
                fontFamily="Poppins, sans-serif"
                fontSize="16px"
                fontWeight="500"
                lineHeight="24px"
                color="#37456C"
                opacity={isExpanded ? 1 : 0}
                transition="opacity 0.2s ease"
                whiteSpace="nowrap"
              >
                {item.label}
              </Text>
            </Flex>
          ))}

          {/* Financial Insights with Dropdown - only show if there are permitted items */}
          {(financialInsightsItems.length > 0 ||
            hasPermission('financialAnalysis')) && (
            <Box>
              <Flex
                align="center"
                p={3}
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: activeBgColor }}
                onClick={() => setFinancialInsightsOpen(!financialInsightsOpen)}
                bg={
                  isFinancialInsightsView(view) ? activeBgColor : 'transparent'
                }
                color={
                  isFinancialInsightsView(view) ? activeTextColor : textColor
                }
              >
                <Box mr={3}>
                  {renderIcon(
                    'financialinsights',
                    ReportsIcon,
                    ReportsSolidIcon
                  )}
                </Box>
                <Text
                  fontFamily="Poppins, sans-serif"
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="24px"
                  color="#37456C"
                  flex={1}
                  opacity={isExpanded ? 1 : 0}
                  transition="opacity 0.2s ease"
                  whiteSpace="nowrap"
                >
                  Financial Insights
                </Text>
                {isExpanded && (
                  <Icon
                    as={financialInsightsOpen ? FiChevronUp : FiChevronDown}
                  />
                )}
              </Flex>
              {financialInsightsOpen && isExpanded && (
                <Box pl={12} py={2}>
                  {financialInsightsItems.map((item, index) => (
                    <Flex
                      key={index}
                      align="center"
                      p={3}
                      borderRadius="lg"
                      cursor="pointer"
                      _hover={{ bg: activeBgColor }}
                      onClick={item.onClick}
                      bg={view === item.key ? activeBgColor : 'transparent'}
                      color={view === item.key ? activeTextColor : textColor}
                    >
                      <Text
                        fontFamily="Poppins, sans-serif"
                        fontSize="16px"
                        fontWeight="500"
                        lineHeight="24px"
                        color="#37456C"
                      >
                        {item.label}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Horizontal Rule */}
          <Box my={3} px={2}>
            <Box height="1px" bg={borderColor} width="100%" />
          </Box>

          {/* Settings Section - only show if there are permitted items */}
          {(settingsItems.length > 0 || hasPermission('setting')) && (
            <Box>
              <Flex
                align="center"
                p={3}
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: activeBgColor }}
                onClick={() => setSettingsOpen(!settingsOpen)}
                bg={isSettingsView(view) ? activeBgColor : 'transparent'}
                color={isSettingsView(view) ? activeTextColor : textColor}
              >
                <Box mr={3}>
                  {renderIcon('settings', SettingsIcon, SettingsSolidIcon)}
                </Box>
                <Text
                  fontFamily="Poppins, sans-serif"
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="24px"
                  color="#37456C"
                  flex={1}
                  opacity={isExpanded ? 1 : 0}
                  transition="opacity 0.2s ease"
                  whiteSpace="nowrap"
                >
                  Settings
                </Text>
                {isExpanded && (
                  <Icon as={settingsOpen ? FiChevronUp : FiChevronDown} />
                )}
              </Flex>

              {settingsOpen && isExpanded && (
                <Box pl={12} py={2}>
                  {settingsItems.map((item, index) => (
                    <Flex
                      key={index}
                      align="center"
                      p={3}
                      borderRadius="lg"
                      cursor="pointer"
                      _hover={{ bg: activeBgColor }}
                      onClick={item.onClick}
                      bg={view === item.key ? activeBgColor : 'transparent'}
                      color={view === item.key ? activeTextColor : textColor}
                    >
                      <Text
                        fontFamily="Poppins, sans-serif"
                        fontSize="16px"
                        fontWeight="500"
                        lineHeight="24px"
                        color="#37456C"
                      >
                        {item.label}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* SpotOn Dashboard - only show for SpotOn customers */}
          {isSpotOnCustomer && (
            <>
              {/* Horizontal Rule */}
              <Box my={3} px={2}>
                <Box height="1px" bg={borderColor} width="100%" />
              </Box>

              <Flex
                align="center"
                p={3}
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: activeBgColor }}
                onClick={() => {
                  // Track the navigation event
                  trackEvent(AnalyticsEventNames.RETURNED_TO_DASHBOARD, {
                    environment: isDevOrQA ? 'qa' : 'production',
                    dashboardUrl: spotOnDashboardUrl,
                  });
                  // Open the dashboard
                  window.open(
                    spotOnDashboardUrl,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
                transition="all 0.2s ease"
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Track the navigation event
                    trackEvent(AnalyticsEventNames.RETURNED_TO_DASHBOARD, {
                      environment: isDevOrQA ? 'qa' : 'production',
                      dashboardUrl: spotOnDashboardUrl,
                      inputMethod: 'keyboard',
                    });
                    // Open the dashboard
                    window.open(
                      spotOnDashboardUrl,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }
                }}
              >
                <Box mr={3}>
                  <GridViewIcon
                    size="1.5rem"
                    color={theme.tokens.semanticColorTextNeutralStaticNone}
                  />
                </Box>
                <Flex
                  align="center"
                  gap="8px"
                  opacity={isExpanded ? 1 : 0}
                  transition="opacity 0.2s ease"
                >
                  <Text
                    fontFamily="Poppins, sans-serif"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="24px"
                    color="#37456C"
                    whiteSpace="nowrap"
                  >
                    SpotOn Dashboard
                  </Text>
                  <ExternalLinkIcon
                    size="1rem"
                    color={theme.tokens.semanticColorTextNeutralStaticNone}
                  />
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default ReskinnedSidebar;
